import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Heart, ImageOff, Pencil, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import type { ArtworkView } from "../../shared/contracts";
import { api } from "../api";
import { useAuth } from "../auth";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageHeader } from "../components/PageHeader";

const FILTERS = [
  { value: "all", label: "全部" },
  { value: "desktop", label: "桌面" },
  { value: "mobile", label: "手机" },
  { value: "favorite", label: "已收藏" },
] as const;

export function ArtworksPage() {
  const { me, isLoading, requestLogin } = useAuth();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["value"]>("all");
  const [deleteTarget, setDeleteTarget] = useState<ArtworkView | null>(null);
  const [preview, setPreview] = useState<ArtworkView | null>(null);
  const [renameTarget, setRenameTarget] = useState<ArtworkView | null>(null);
  const [undo, setUndo] = useState<{ id: string; title: string } | null>(null);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["artworks", filter],
    queryFn: () => api.artworks(filter),
    enabled: Boolean(me),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["artworks"] });
    void queryClient.invalidateQueries({ queryKey: ["me"] });
  };
  const favorite = useMutation({ mutationFn: (artwork: ArtworkView) => api.updateArtwork(artwork.id, { isFavorite: !artwork.isFavorite }), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: (artwork: ArtworkView) => api.deleteArtwork(artwork.id),
    onSuccess: (_, artwork) => { setUndo({ id: artwork.id, title: artwork.title }); setDeleteTarget(null); refresh(); window.setTimeout(() => setUndo(null), 10_000); },
  });
  const restore = useMutation({ mutationFn: (id: string) => api.restoreArtwork(id), onSuccess: () => { setUndo(null); refresh(); } });
  const download = useMutation({ mutationFn: (id: string) => api.downloadArtwork(id), onSuccess: (result) => { void api.track("artwork_downloaded", { variant: result.variant }); window.location.assign(result.url); } });
  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => api.updateArtwork(id, { title }),
    onSuccess: () => { setRenameTarget(null); refresh(); },
  });

  if (!isLoading && !me) {
    return <GuestLibrary onLogin={() => requestLogin("/artworks")} />;
  }

  return (
    <section>
      <PageHeader
        eyebrow="My Creations"
        title="我的作品"
        subtitle="你生成的每个世界，都收藏在这里。"
        actions={<div className="filter-row" role="group" aria-label="作品筛选">{FILTERS.map((item) => <button key={item.value} className={filter === item.value ? "filter-chip active" : "filter-chip"} onClick={() => setFilter(item.value)}>{item.label}</button>)}</div>}
      />

      {query.isLoading && <div className="gallery-grid skeleton-grid">{Array.from({ length: 6 }, (_, index) => <div key={index} className="art-card skeleton" />)}</div>}
      {query.isError && <div className="empty-state"><ImageOff size={34} /><h2>作品暂时无法加载</h2><p>请检查网络连接后重试。</p><button className="button button-secondary" onClick={() => query.refetch()}>重新加载</button></div>}
      {query.data?.items.length === 0 && <div className="empty-state"><Sparkles size={34} /><h2>这里还没有作品</h2><p>从一句画面描述开始，创建你的第一张游戏壁纸。</p><a className="button button-primary" href="/generate">开始生成</a></div>}
      {query.data && query.data.items.length > 0 && (
        <div className="gallery-grid">
          {query.data.items.map((artwork) => (
            <article key={artwork.id} className="art-card" style={{ backgroundImage: "linear-gradient(0deg, rgba(5,7,10,.96), transparent 66%), url(" + artwork.thumbnailUrl + ")" }}>
              <button className="art-open" onClick={() => setPreview(artwork)} aria-label={"预览作品 " + artwork.title} />
              <div className="art-meta"><div><strong>{artwork.title}</strong><span>{artwork.width} × {artwork.height}</span></div><div className="art-actions">
                <button className={artwork.isFavorite ? "mini-action active" : "mini-action"} onClick={() => favorite.mutate(artwork)} aria-label={artwork.isFavorite ? "取消收藏" : "收藏"}><Heart size={15} fill={artwork.isFavorite ? "currentColor" : "none"} /></button>
                <button className="mini-action" onClick={() => download.mutate(artwork.id)} aria-label="下载"><Download size={15} /></button>
                <button className="mini-action" onClick={() => setRenameTarget(artwork)} aria-label="重命名"><Pencil size={15} /></button>
                <button className="mini-action danger" onClick={() => setDeleteTarget(artwork)} aria-label="删除"><Trash2 size={15} /></button>
              </div></div>
            </article>
          ))}
        </div>
      )}

      {undo && <div className="undo-toast" role="status"><span>已删除“{undo.title}”</span><button onClick={() => restore.mutate(undo.id)} disabled={restore.isPending}>撤销</button><button className="toast-close" onClick={() => setUndo(null)} aria-label="关闭"><X size={15} /></button></div>}
      <ConfirmDialog open={Boolean(deleteTarget)} title="删除这张作品？" description="删除后 10 秒内可以撤销，随后作品会从作品库中隐藏。" confirmLabel="确认删除" tone="danger" busy={remove.isPending} onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && remove.mutate(deleteTarget)} />
      <RenameDialog artwork={renameTarget} busy={rename.isPending} onClose={() => setRenameTarget(null)} onSave={(title) => renameTarget && rename.mutate({ id: renameTarget.id, title })} />
      <PreviewModal artwork={preview} onClose={() => setPreview(null)} onDownload={(id) => download.mutate(id)} />
    </section>
  );
}

function GuestLibrary({ onLogin }: { onLogin(): void }) {
  return <section><PageHeader eyebrow="My Creations" title="我的作品" subtitle="登录后，生成的世界会自动收藏在这里。" /><div className="empty-state guest-state"><span className="kook-mark">K</span><h2>使用 KOOK 登录查看作品库</h2><p>只读取基础用户信息，不会获取你的 KOOK 密码。</p><button className="button button-primary" onClick={onLogin}>使用 KOOK 登录</button></div></section>;
}

function RenameDialog({ artwork, busy, onClose, onSave }: { artwork: ArtworkView | null; busy: boolean; onClose(): void; onSave(title: string): void }) {
  const [title, setTitle] = useState(artwork?.title ?? "");
  useEffect(() => { setTitle(artwork?.title ?? ""); }, [artwork]);
  if (!artwork) return null;
  return <div className="modal-backdrop"><form className="modal rename-modal" role="dialog" aria-modal="true" aria-labelledby="rename-title" onSubmit={(event) => { event.preventDefault(); onSave(title.trim()); }}><header className="modal-header"><div><p className="eyebrow">Edit artwork</p><h2 id="rename-title">修改作品标题</h2></div><button type="button" className="icon-button" onClick={onClose} aria-label="关闭"><X size={19} /></button></header><label className="field-label" htmlFor="artwork-title"><span>作品标题</span><small>{title.length} / 80</small></label><input id="artwork-title" className="text-input" value={title} maxLength={80} autoFocus onChange={(event) => setTitle(event.target.value)} /><div className="dialog-actions"><button type="button" className="button button-secondary" onClick={onClose}>取消</button><button className="button button-primary" disabled={!title.trim() || busy}>{busy ? "保存中…" : "保存"}</button></div></form></div>;
}

function PreviewModal({ artwork, onClose, onDownload }: { artwork: ArtworkView | null; onClose(): void; onDownload(id: string): void }) {
  if (!artwork) return null;
  return <div className="modal-backdrop preview-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><section className="preview-modal" role="dialog" aria-modal="true" aria-label={artwork.title}><button className="preview-close icon-button" onClick={onClose} aria-label="关闭预览"><X size={20} /></button><img src={artwork.previewUrl} alt={artwork.title} /><footer><div><h2>{artwork.title}</h2><p>{artwork.width} × {artwork.height} · {artwork.aspectRatio}</p></div><button className="button button-primary" onClick={() => onDownload(artwork.id)}><Download size={17} />下载壁纸</button></footer></section></div>;
}
